import Modal from '../modal/modal'
import { bodyElement } from '../common/body-element'
import { type Nullable } from '../../types/common'
import { PopupStorage } from './popup-storage'
import { PopupType } from './types'
import { POPUP_ANIMATION_DELAY, gradientOpacify } from './utils'
import { ConditionRuntime } from './condition-runtime'
import { POPUP_CLASSNAMES } from './constants'
import { checkMobileVersion } from '../../utils/check-media'

export class PopupAgeVerifier extends Modal {
  storage: PopupStorage
  conditionRuntime?: ConditionRuntime
  hasImage: boolean;
  hideImgOnMobile: boolean;

  constructor() {
    super()
    this.storage = new PopupStorage(this.dataset.blockId ?? this.id)
    this.hasImage = this.dataset.hasImage === 'true'
    this.hideImgOnMobile = this.dataset.hideImgMobile === 'true'
  }

  connectedCallback(): void {
    const data = this.storage.loadData()

    if (this.isEditorMode) {
      this.editor.subscribe('BLOCK_SELECT', this._handleBlockSelect)
      this.editor.subscribe('BLOCK_DESELECT', this._handleBlockDeselect)
      return
    }

    if (!data || (data.type === PopupType.AgeVerifier && !data.verified)) {
      this.conditionRuntime = new ConditionRuntime(
        this.dataset,
        null,
        this.show,
      )
      this.conditionRuntime.run()
    }
  }

  disconnectedCallback(): void {
    if (this.isEditorMode) {
      this.editor.destroy()
    }
    this.conditionRuntime?.cleanup()
  }

  _handleBlockSelect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.show()
    }
  }

  _handleBlockDeselect = ({ detail: { blockId } }): void => {
    if (this.dataset.blockId === blockId) {
      this.hide()
    }
  }

  show = async ():Promise<void> => {
    const withGradient = !!this.dataset.overlayGradient

    const background = withGradient
    ? gradientOpacify(
        this.dataset.overlayGradient,
        this.dataset.overlayOpacity,
      )
    : `color-mix(in srgb, ${this.dataset.overlaySolid} ${
        this.dataset.overlayOpacity * 100
      }%, transparent)`

    bodyElement.showOverlay(this.id, {
      zIndex: 15,
      background,
      backdropFilter: `blur(${this.dataset.overlayBlur}px)`,
      transitionDuration: '0s',
    })

    await super.show()

    this.classList.add(POPUP_CLASSNAMES.lightboxActive)

    if (this.hasImage) {
      if (this.hideImgOnMobile && checkMobileVersion()) {
        return
      }
      
      setTimeout(() => {
        this.classList.add(POPUP_CLASSNAMES.showImage)
      }, POPUP_ANIMATION_DELAY)
    }

    const closeBtn = this.querySelector(".popup__close-button") as HTMLElement;
    if (closeBtn) {
      closeBtn.style.display = "flex"
    }

    this._verifyBtn?.addEventListener('click', this.onVerify)
  }

  hide = async (): Promise<void> => {
    bodyElement.hideOverlay(this.id)

    this.conditionRuntime?.cleanup()

    this.classList.remove(POPUP_CLASSNAMES.lightboxActive)
    this.classList.add(POPUP_CLASSNAMES.lightboxClosing)

    await super.hide()
    this.classList.remove(POPUP_CLASSNAMES.showImage)
    this.classList.remove(POPUP_CLASSNAMES.lightboxClosing)
  }

  onVerify = (): void => {
    if (!this.isEditorMode) {
      this.storage.saveData({ type: PopupType.AgeVerifier, verified: true })
    }
    this._verifyBtn?.removeEventListener('click', this.onVerify)
    this.hide()
  }

  get _verifyBtn(): Nullable<HTMLElement> {
    if (!this.isOpen) {
      return null
    }

    return this.querySelector('[data-verify-btn]')
  }
}
